/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Which agent should I use?", description = "Understand the difference between the Agent in the audience builder and the standalone Agents workspace, and choose the right one for your task.", lastUpdatedDate = new Date(1787702400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Which agent should I use?",
        description: "Understand the difference between the Agent in the audience builder and the standalone Agents workspace, and choose the right one for your task.",
        lastUpdatedDate: "Wed Aug 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "At a glance",
          "depth": 0,
          "id": "at-a-glance"
        }, {
          "value": "Agent in the audience builder",
          "depth": 0,
          "id": "agent-in-the-audience-builder"
        }, {
          "value": "What you can do",
          "depth": 1,
          "id": "what-you-can-do"
        }, {
          "value": "What it doesn't do",
          "depth": 1,
          "id": "what-it-doesnt-do"
        }, {
          "value": "Standalone Agents",
          "depth": 0,
          "id": "standalone-agents"
        }, {
          "value": "What you can do",
          "depth": 1,
          "id": "what-you-can-do-1"
        }, {
          "value": "Choose the right one",
          "depth": 0,
          "id": "choose-the-right-one"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      hr: "hr",
      h2: "h2",
      img: "img",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams, platform admins"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch has two agents for different kinds of work: the ", _jsx(_components.strong, {
          children: "Agent in the audience builder"
        }), " and the ", _jsx(_components.strong, {
          children: "standalone Agents workspace"
        }), ". One helps you build audiences inside Customer Studio, and the other helps you answer broader questions, analyze performance, and automate recurring work."]
      }), "\n", _jsx(_components.p, {
        children: "Use the Agent in the audience builder when you already know the audience you want to build. Use Agents when you're starting with a question and need to analyze data before deciding what to do next."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["This page covers the Agent in the audience builder and the standalone Agents workspace. It does not cover ", _jsx(_components.a, {
            href: "/ai-decisioning/overview",
            children: "AI Decisioning"
          }), " agents, which optimize message delivery within campaigns."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "at-a-glance",
        children: "At a glance"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {
              children: "Agent in the audience builder"
            }), _jsx(_components.th, {
              children: "Standalone Agents"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Where to find it"
              })
            }), _jsx(_components.td, {
              children: "Inside the audience builder when creating or editing an audience"
            }), _jsxs(_components.td, {
              children: ["The ", _jsx(_components.strong, {
                children: "Agents"
              }), " tab in the left navigation"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "What it does"
              })
            }), _jsx(_components.td, {
              children: "Builds, modifies, and explains audiences using natural language"
            }), _jsx(_components.td, {
              children: "Explores customer data, analyzes campaign performance, automates reporting, and builds audiences"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Best for"
              })
            }), _jsx(_components.td, {
              children: "You already know the audience you need and want help defining it — creating filters, understanding changes, exploring fields"
            }), _jsx(_components.td, {
              children: "You're starting with a question and want to explore data, get recommendations, or automate reporting"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Availability"
              })
            }), _jsxs(_components.td, {
              children: ["Available with ", _jsx(_components.a, {
                href: "/customer-studio/overview",
                children: "Customer Studio"
              })]
            }), _jsxs(_components.td, {
              children: ["Available in workspaces with access to Agents, including workspaces that use ", _jsx(_components.a, {
                href: "https://hightouch.com/platform/agentic-marketing-platform",
                children: "Ad Studio or Lifecycle Studio"
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "agent-in-the-audience-builder",
        children: "Agent in the audience builder"
      }), "\n", _jsx(_components.p, {
        children: "The Agent in the audience builder is available when you create a new audience or edit an existing one. It's scoped to the audience you're currently working on. Use it to build filter conditions, explain why an audience changed size, and explore available fields and traits."
      }), "\n", _jsx(_components.p, {
        children: "This agent is designed for moments when you already know what audience you need and want help defining it. It translates natural-language descriptions into audience filters and helps you find the fields, traits, and events available in your schema."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/agents/build-audience-natural-language.png",
          alt: "The Agent panel alongside the audience builder"
        })
      }), "\n", _jsx(_components.h3, {
        id: "what-you-can-do",
        children: "What you can do"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Build audiences:"
          }), " Describe the audience you want (for example, \"users who opened an email but didn't click in the last 7 days\") and the Agent creates the filter conditions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Understand audiences:"
          }), " Ask the Agent to summarize an audience, break it down by attributes, or explain what caused a size change."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Explore your schema:"
          }), " Ask what fields, traits, or events are available for filtering — for example, \"What columns can I use to filter by age?\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Refine definitions:"
          }), " Follow up with adjustments like \"add users who purchased in the last 30 days\" and the Agent updates the filters."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "what-it-doesnt-do",
        children: "What it doesn't do"
      }), "\n", _jsx(_components.p, {
        children: "The Agent in the audience builder focuses on audience mechanics, not broader strategy. It won't recommend which audiences to target for a campaign, predict conversion likelihood, or provide open-ended analysis. If you need that kind of guidance, use the standalone Agents workspace."
      }), "\n", _jsxs(_components.p, {
        children: ["See the ", _jsx(_components.a, {
          href: "/customer-studio/agents",
          children: "Audience builder prompting guide"
        }), " for full documentation."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "standalone-agents",
        children: "Standalone Agents"
      }), "\n", _jsx(_components.p, {
        children: "The standalone Agents workspace is where you ask open-ended questions, analyze campaign performance, and create recurring reports. It connects to your data warehouse and marketing channels to answer questions about your customers and campaigns."
      }), "\n", _jsx(_components.p, {
        children: "Unlike the Agent in the audience builder, standalone Agents aren't scoped to a single audience. You can ask broad questions, compare datasets, get strategic recommendations, and save prompts for recurring use."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/overview/overview.png",
          alt: "The standalone Agents chat interface"
        })
      }), "\n", _jsx(_components.h3, {
        id: "what-you-can-do-1",
        children: "What you can do"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Chat:"
          }), " Ask natural-language questions about customer data, campaign performance, and marketing strategy. The agent returns answers with charts, tables, and recommendations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Reports:"
          }), " Save prompts that run on a schedule and deliver results via email or Slack — useful for weekly summaries, daily dashboards, and automated monitoring."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom agents:"
          }), " Create specialized agents scoped to specific teams or use cases with defined tasks, tone, and guardrails."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Strategic analysis:"
          }), " Get targeting recommendations, identify opportunities, analyze creative performance, and design experiments."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Build audiences from insights:"
          }), " After analysis, ask the agent to draft an audience for you to review and save in Customer Studio."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Standalone Agents does not replace the campaign creation workflows in Ad Studio or Lifecycle Studio. See ", _jsx(_components.a, {
          href: "/agents/overview",
          children: "Agents overview"
        }), " for full documentation."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "choose-the-right-one",
        children: "Choose the right one"
      }), "\n", _jsx(_components.p, {
        children: "Use the Agent in the audience builder when you already know the audience you need. Use standalone Agents when you're starting from a question."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Scenario"
            }), _jsx(_components.th, {
              children: "Recommended agent"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "\"Build an audience of users who opened but didn't click in 7 days\""
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agent in the audience builder"
              }), " — this is a specific audience definition task"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "\"What fields can I use to filter by age?\""
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agent in the audience builder"
              }), " — exploring schema for the current audience"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "\"Why did this audience shrink?\""
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agent in the audience builder"
              }), " for filter or data-change explanations. Use ", _jsx(_components.strong, {
                children: "Agents"
              }), " if you need broader root-cause analysis and recommendations."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "\"Who should I target for this campaign given these goals?\""
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agents"
              }), " — this requires analysis across your customer and campaign data"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "\"How likely is this audience to convert?\""
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agents"
              }), " — requires analysis of past campaign performance and customer behavior"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "\"What should we do to improve our email performance?\""
            }), _jsxs(_components.td, {
              children: [_jsx(_components.strong, {
                children: "Agents"
              }), " — open-ended question requiring cross-dataset analysis"]
            })]
          })]
        })]
      })]
    });
  }
}
export default MDXContent;
